import { PlusIcon } from "@xola/icons";
import { kebabCase, uniqueId } from "lodash";
import React, { useState } from "react";
import { BaseSchedule, BaseScheduleType, Schedule } from "types";
import { BaseScheduleButtonGroup } from "@/components/BaseSchedule/BaseScheduleButtonGroup";
import { BaseScheduleEditorModal } from "@/components/BaseSchedule/BaseScheduleEditorModal";
import { defaultValues } from "@/components/BaseSchedule/BaseScheduleInput";
import { BaseScheduleSummary } from "@/components/BaseSchedule/BaseScheduleSummary";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { useVisibility } from "@/hooks/useVisibility";

interface MembershipScheduleInputProps {
    title: string;
    description?: string;
    schedules: BaseSchedule[];
    onChange: (schedules: BaseSchedule[]) => void;
    type?: BaseScheduleType;
}

const getDefaultSchedule = (type: BaseScheduleType): Schedule => ({
    ...defaultValues,
    allowedPrivacies: [],
    type,
});

export const MembershipScheduleInput = ({
    title,
    description,
    schedules,
    onChange,
    type = BaseScheduleType.AVAILABLE,
}: MembershipScheduleInputProps) => {
    const [scheduleToBeEdited, setScheduleToBeEdited] = useState<BaseSchedule | null>(null);
    const [isScheduleEditorOpen, { show: showScheduleEditor, hide: hideScheduleEditor }] = useVisibility(false);

    const handleApply = (updatedSchedule: BaseSchedule) => {
        if (updatedSchedule?.id) {
            onChange(
                schedules.map((schedule) => {
                    if (schedule.id === updatedSchedule.id) {
                        return { ...updatedSchedule };
                    }

                    return schedule;
                }),
            );

            return;
        }

        /** A new window is created */
        onChange([...schedules, { ...updatedSchedule, id: getDraftId() }]);
    };

    const handleClose = () => {
        hideScheduleEditor();
        setScheduleToBeEdited(null);
    };

    const handleDelete = ({ id }: BaseSchedule) => {
        onChange(schedules.filter((schedule) => schedule.id !== id));
    };

    const handleEdit = (schedule: BaseSchedule) => {
        setScheduleToBeEdited(schedule);
        showScheduleEditor();
    };

    return (
        <>
            <div className="mb-5" data-testid={`membership-schedule-input-${type}`}>
                <div className="!m-0 flex items-center justify-between">
                    <h4 className="font-bold">
                        {type === BaseScheduleType.AVAILABLE ? "Window Definition" : "Blackout Restrictions"}
                    </h4>
                    <SecondaryButton
                        size="small"
                        icon={<PlusIcon size="small" />}
                        data-testid={`add-${kebabCase(title)}-schedule-button`}
                        onClick={showScheduleEditor}
                    >
                        Add {title}
                    </SecondaryButton>
                </div>

                {schedules.length > 0 ? (
                    <div className="my-4 flex flex-col space-y-4">
                        {schedules.map((schedule, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <div key={index} className="rounded-xl border border-gray-light p-4 hover:bg-gray-light">
                                <BaseScheduleSummary productStyling schedule={schedule}>
                                    <BaseScheduleButtonGroup
                                        schedule={schedule}
                                        handleEditButton={handleEdit}
                                        onDeleteSchedule={handleDelete}
                                    />
                                </BaseScheduleSummary>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="mt-1 text-gray-darker">No restrictions</div>
                )}
            </div>

            <BaseScheduleEditorModal
                title={title}
                description={description}
                isOpen={isScheduleEditorOpen}
                initialValues={scheduleToBeEdited ?? getDefaultSchedule(type)}
                weekdaysType="button"
                onClose={handleClose}
                onApply={handleApply}
            />
        </>
    );
};

const getDraftId = () => uniqueId("draft:");
